import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '../openapi/openapi';
import { useApi, useApiClient } from '../contexts/ApiContext';
import { ApiAuthRequest } from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { mapResponse } from './helpers';

export function useAuthMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApi();
  return useMutation({
    mutationKey: ['auth'],
    mutationFn: (body: ApiAuthRequest) =>
      api.client
        .POST('/auth/notOnlyActive', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess(data) {
      if (data.type === 'CHALLENGES_COMPLETED') {
        api.setBusinessId(null);
        api.setAccessToken(data.accessToken ?? null);
        api.setRefreshToken(data.refreshToken ?? null);
      }
    },
  });
}

export function useOtpResendMutation() {
  const client = useApiClient();
  return useMutation({
    mutationKey: ['auth'],
    mutationFn: (body: components['schemas']['OtpResourceRequestsOtpResend']) =>
      client
        .POST('/otp/resend', {
          body,
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
  });
}

export function useLogoutMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['logout'],
    mutationFn: () =>
      api.client.POST('/auth/logout', { headers: { 'Idempotency-Key': idempotencyKey } }),
    retry: true,
    onSuccess() {
      queryClient.clear();
      api.setAccessToken(null);
      api.setRefreshToken(null);
    },
  });
}

export function useSwitchBusinessMutation() {
  const { setBusinessId } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['switch-business'],
    mutationFn: setBusinessId,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}
